import type { FC, MouseEvent } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Divider, IconButton } from '@mui/material';

import type { DetailedFollowUpTicket } from 'src/types/followUp';
import { internationalDateFormat } from 'src/helper/helper';
import { useFollowUpPermissions } from 'src/components/app/context/UserContext/useFollowUpPermissions';
import ChangeOwnerTicketDetailModal from 'src/components/followUp/TicketOverviewPage/TicketDetail/Sidebar/ChangeOwnerTicketDetailModal';
import NpsScore from 'src/components/followUp/TicketOverviewPage/TicketTable/Cells/NpsScore';

import RespondentBio from './RespondentBio';
import SidebarSection from './SidebarSection';
import TicketDetailBox from './TicketDetailBox';

interface TicketDataSectionProps {
  ticket: DetailedFollowUpTicket;
  refetchList: () => void;
}

const TicketDataSection: FC<TicketDataSectionProps> = ({
  ticket,
  refetchList,
}) => {
  const { t } = useTranslation('followUp');
  const { userCanEditFollowUps, loading: userCanEditFollowUpsLoading } =
    useFollowUpPermissions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const followUpOwnerName = useMemo(() => {
    if (!ticket.owner && !ticket.respondent)
      // Ticket was anonymized.
      return t('changelog.deletedUser');

    if (ticket.owner)
      return `${ticket.owner.firstName} ${ticket.owner.lastName}`;

    // Ticket was imported with unavailable follow-up owner.
    return '';
  }, [ticket]);

  const onEditOwnerClick = (e: MouseEvent<HTMLElement>) =>
    setAnchorEl((prev) => (prev ? null : e.currentTarget));

  return (
    <SidebarSection>
      <TicketDetailBox
        label={
          <Box
            component="span"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {t('tableHeader.followUpOwner')}
            {!userCanEditFollowUpsLoading && userCanEditFollowUps && (
              <IconButton onClick={onEditOwnerClick}>
                <EditOutlinedIcon color="primary" fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
        value={followUpOwnerName}
      />
      <Divider />
      <TicketDetailBox
        label={t('tableHeader.firstContact')}
        value={
          ticket.followUpCall?.firstContactAt
            ? internationalDateFormat(
                new Date(ticket.followUpCall.firstContactAt),
              )
            : '-'
        }
      />
      <TicketDetailBox
        label={t('createdDate')}
        value={internationalDateFormat(new Date(ticket.createdAt))}
      />
      <Divider />
      <TicketDetailBox
        label={t('npsGroupAndScore')}
        value={
          <>
            {ticket.npsGroup}
            <NpsScore score={ticket.npsScore} />
          </>
        }
      />
      <RespondentBio respondent={ticket.respondent} />
      <ChangeOwnerTicketDetailModal
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        ticket={ticket}
        refetchList={refetchList}
      />
    </SidebarSection>
  );
};

export default TicketDataSection;
